/**
 * ONE reader of `changeSubscription`'s answer, for every page that changes a live plan (/subscription's adjuster,
 * /pricing's hire card, and the pricing page's rebuild in the new design).
 *
 * WHY IT EXISTS (2026-09-30). The server refuses a change it cannot make on the card on file with a 409 or a 402 AND
 * `needsCheckout: true` in the body (functions/src/subscriptions.ts). /subscription read the body whatever the status
 * and opened its card popup; /pricing read the body only on a 2xx (`resp.ok ? await resp.json() : null`), so its
 * "a change that needs a fresh checkout runs on the subscription page" branch could never run and the person was told
 * "that didn't go through" instead. Two readings of one answer is how that happened, so there is one now.
 *
 * Never throws: every outcome, a missing session and a network failure included, is a value.
 */
import { fnUrl, idTokenFor } from './billingSnapshotCache';

export type PlanChangeTerm = 'monthly' | 'annual' | 'prepay';

export interface PlanChangeRequest {
  mode: 'preview' | 'apply';
  term: PlanChangeTerm;
  seats: number;
  /** The organization being changed; absent for a personal plan. */
  accountId?: string;
}

export type PlanChangeAnswer =
  | {
      kind: 'ok';
      /** 'now' when the change applies at once; otherwise when it lands. */
      effectiveAt: string;
      isUpgrade?: boolean;
      amountDueNowCents?: number;
      /** Unix seconds: the end of the current term, when the server names it. */
      periodEnd?: number;
    }
  /** The card on file cannot carry it (declined, or no live subscription): a checkout is needed. */
  | { kind: 'needsCheckout'; error: string }
  | { kind: 'failed'; error: string }
  | { kind: 'signed-out' };

export interface PlanChangeDeps {
  fetch: typeof fetch;
  getToken: () => Promise<string | null>;
}

/**
 * The signed-in person's token, waiting briefly for a session still being restored (idTokenFor's contract). firebase/auth
 * is loaded when asked for, never at this module's load: it touches `fetch` as it loads, and suites that render a page
 * using this client would otherwise fail before a test runs (the chat-isolation trap, CLAUDE.md).
 */
async function signedInToken(): Promise<string | null> {
  const { getAuth } = await import('firebase/auth');
  return idTokenFor(getAuth());
}

const DEFAULT_DEPS: PlanChangeDeps = {
  fetch: (...args) => fetch(...args),
  getToken: signedInToken,
};

export async function requestPlanChange(
  request: PlanChangeRequest,
  deps: PlanChangeDeps = DEFAULT_DEPS,
): Promise<PlanChangeAnswer> {
  let token: string | null = null;
  try {
    token = await deps.getToken();
  } catch {
    token = null;
  }
  if (!token) return { kind: 'signed-out' };
  try {
    const resp = await deps.fetch(fnUrl('changeSubscription'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        mode: request.mode,
        term: request.term,
        seats: request.seats,
        ...(request.accountId ? { accountId: request.accountId } : {}),
      }),
    });
    // The body carries the verdict on EVERY status: needsCheckout rides a 409 or a 402.
    const data = (await resp.json().catch(() => ({}))) as Record<string, unknown> | null;
    const body = data && typeof data === 'object' ? data : {};
    const error = typeof body.error === 'string' && body.error ? body.error : `HTTP ${resp.status}`;
    if (body.needsCheckout === true) return { kind: 'needsCheckout', error };
    if (!resp.ok) return { kind: 'failed', error };
    return {
      kind: 'ok',
      effectiveAt: typeof body.effectiveAt === 'string' ? body.effectiveAt : '',
      ...(typeof body.isUpgrade === 'boolean' ? { isUpgrade: body.isUpgrade } : {}),
      ...(typeof body.amountDueNowCents === 'number' ? { amountDueNowCents: body.amountDueNowCents } : {}),
      ...(typeof body.periodEnd === 'number' ? { periodEnd: body.periodEnd } : {}),
    };
  } catch (e) {
    return { kind: 'failed', error: e instanceof Error ? e.message : 'network' };
  }
}
